import React from 'react';
import { Box, Text, HStack, Icon, Flex } from '@chakra-ui/react';
import { FiCheck, FiLink, FiRadio, FiUsers, FiSend, FiInbox } from 'react-icons/fi';
import Card from 'components/card/Card';

/**
 * Where a workspace is in getting WhatsApp working, as five steps.
 *
 * ★ THE SCREEN KNEW ALL OF THIS AND SAID NONE OF IT.
 * Every fact below was already on the page, scattered across a status pill, a
 * webhook card and each number card — so an administrator could read four
 * separate half-answers and still not know what to do next. That is what made
 * a connected account feel stuck: nothing said "you are three quarters of the
 * way, and the remaining step is assignment".
 *
 * ★★ THE WEBHOOK COMES LAST, AND THAT ORDER IS DELIBERATE.
 * It used to sit second, which put the hardest step — publishing this service
 * at a public HTTPS address Meta can reach — in front of everything a workspace
 * can actually finish on its own. Sending needs only the token and an
 * assignment; the webhook is needed to RECEIVE. So the order now follows what a
 * workspace can prove, in the order it can prove it: the number exists, someone
 * can see it, something really went out, and only then can a customer write
 * back. A client with no public address yet still reaches three of four and
 * knows exactly which one is left, instead of stalling at step two.
 *
 * ★ DERIVED, NEVER STORED. Each step is a question asked of state that already
 * exists, so it cannot drift from what the cards below it show. There is no
 * "setup completed" flag anywhere, deliberately: a number whose people are all
 * removed later is genuinely not finished any more, and this says so.
 *
 * ★ WHICH IS ALSO WHY FAILURE NEEDS NO RETRY LOGIC. Five independent
 * questions cannot reset one another: a webhook that stops verifying cannot
 * un-send the message that already went out, and changing an assignment cannot
 * alter the Meta connection. Recovery is answering the question again.
 */

const DONE = 'done';
const CURRENT = 'current';
const TODO = 'todo';

/**
 * @param {object} input
 * @param {Array} input.channels     the CRM's own channel rows
 * @param {object} [input.webhook]   the connection layer's webhook facts
 * @returns {Array<{key, label, hint, icon, state}>}
 */
export function setupSteps({ channels = [], webhook = null } = {}) {
	// A "needs configuration" row is a number the platform routes here that the
	// CRM never wrote. It is a fault to repair, not progress to count.
	const real = channels.filter((c) => !c.needsConfiguration);
	const connected = real.length > 0;

	// ★ ASSIGNED IS NOT THE SAME AS ABLE TO SEE IT. A number whose assignees all
	// lack the WhatsApp module is exactly as invisible as one with nobody on it,
	// so the two failures count as the same unfinished step.
	const staffed = real.filter(
		(c) => (c.assignedUsers ?? 0) > 0 && (c.assignedWithoutAccess ?? 0) < (c.assignedUsers ?? 0),
	);
	const peopleDone = connected && staffed.length === real.length;

	// Something really went out. Proves the workspace's token, the connection
	// layer and Meta's acceptance in one — and needs no webhook at all.
	const sent = real.filter((c) => c.lastOutboundAt);
	const sentDone = sent.length > 0;

	// Meta's own verdict on the callback URL. Tenant-wide, because the
	// handshake carries nothing but a token.
	const verified = Boolean(webhook?.handshakeVerifiedAt);

	// ★ PER NUMBER, NOT PER TENANT.
	// This used to read the tenant-wide webhook row, which is unique per
	// tenant — so with two numbers, one busy and one silent, it called both
	// of them receiving. A workspace verifying its second number watched a
	// tick that had been green before it started. The connection layer now
	// stamps the number a customer actually wrote to.
	const received = real.some((c) => c.lastInboundAt);

	const steps = [
		{
			key: 'connected',
			label: 'Number connected',
			icon: FiLink,
			hint: connected
				? `${real.length} number${real.length === 1 ? '' : 's'}`
				: 'Connect your WhatsApp Business Account',
			done: connected,
		},
		{
			key: 'people',
			label: 'People assigned',
			icon: FiUsers,
			hint: !connected
				? 'After a number is connected'
				: peopleDone
					? 'Everyone can see their number'
					: `${real.length - staffed.length} number${real.length - staffed.length === 1 ? '' : 's'} nobody can see`,
			done: peopleDone,
		},
		{
			key: 'sent',
			label: 'Test message sent',
			icon: FiSend,
			// ★ WHAT IS DONE IS ASKED FIRST.
			// A step can complete out of order: one number can be staffed and have
			// sent while another has nobody, which leaves step 2 unfinished and this
			// one genuinely done. Branching on the prerequisite first put a green
			// tick beside the words "needs someone assigned" — the step contradicting
			// itself. The prerequisite is only worth naming while nothing has sent.
			hint: sentDone
				? 'Sending works'
				: peopleDone
					? 'Send a test message from a number below'
					: 'Needs someone assigned to send from',
			done: sentDone,
		},
		{
			// ★ SPLIT FROM THE STEP BELOW, AND THE SPLIT IS THE DIAGNOSIS.
			// These were one step, named after the outcome, because Meta verifies a
			// callback URL happily and then delivers nothing when the `messages`
			// subscription is missing — a single tick would have gone green on a
			// setup that receives silence. Two ticks say more, not less: green here
			// and grey below is precisely the "verified but subscribed to nothing"
			// case, and it is the commonest way this fails. The fifth step is what
			// makes the fourth safe to show.
			//
			// Tenant-wide, unlike the rest: the handshake carries nothing but a
			// token, so Meta's verdict cannot be attributed to one number.
			key: 'webhook',
			label: 'Webhook verified',
			icon: FiRadio,
			hint: verified
				? 'Meta accepted the callback URL'
				: 'Paste the callback URL into your Meta app',
			done: verified,
		},
		{
			// ★ THE ONLY STEP THAT PROVES THE WHOLE PATH.
			// Meta → webhook → tenant → number → CRM → conversation. Everything
			// above can be green while this stays grey, and when it does the answer
			// is almost always an unsubscribed `messages` field.
			key: 'received',
			label: 'Message received',
			icon: FiInbox,
			hint: received
				? 'A customer message has arrived'
				: verified
					? 'Send a WhatsApp message to your number'
					: 'After the webhook is verified',
			done: received,
		},
	];

	// Exactly one step is "current": the first unfinished one. Everything after
	// it is simply ahead, not failing — a screen that marks four things red at
	// once tells nobody where to start.
	const next = steps.findIndex((s) => !s.done);
	return steps.map((s, i) => ({
		...s,
		state: s.done ? DONE : i === next ? CURRENT : TODO,
	}));
}

const TONE = {
	[DONE]: { color: 'success', border: 'success', label: 'text.primary' },
	[CURRENT]: { color: 'text.accent', border: 'text.accent', label: 'text.primary' },
	[TODO]: { color: 'text.muted', border: 'border.subtle', label: 'text.muted' },
};

export default function SetupProgress({ channels, webhook }) {
	const steps = React.useMemo(() => setupSteps({ channels, webhook }), [channels, webhook]);

	return (
		<Card bg="bg.surface" borderWidth="1px" borderColor="border.default">
			<Flex
				direction={{ base: 'column', md: 'row' }}
				align={{ base: 'stretch', md: 'center' }}
				gap={{ base: 3, md: 0 }}
			>
				{steps.map((step, i) => {
					const tone = TONE[step.state];
					return (
						<React.Fragment key={step.key}>
							<HStack spacing="3" flex="1" minW="0" align="center">
								<Flex
									align="center"
									justify="center"
									boxSize="30px"
									flexShrink={0}
									borderRadius="full"
									borderWidth="1px"
									borderColor={tone.border}
									bg={step.state === DONE ? 'bg.subtle' : 'transparent'}
								>
									<Icon as={step.state === DONE ? FiCheck : step.icon} color={tone.color} boxSize="3.5" />
								</Flex>
								<Box minW="0">
									<Text fontSize="sm" fontWeight="medium" color={tone.label} noOfLines={1}>
										{step.label}
									</Text>
									<Text fontSize="xs" color={step.state === CURRENT ? 'text.accent' : 'text.muted'} noOfLines={1}>
										{step.hint}
									</Text>
								</Box>
							</HStack>
							{i < steps.length - 1 && (
								<Box
									display={{ base: 'none', md: 'block' }}
									flex="0 0 24px"
									h="1px"
									mx="2"
									bg={step.state === DONE ? 'success' : 'border.subtle'}
								/>
							)}
						</React.Fragment>
					);
				})}
			</Flex>
		</Card>
	);
}
